'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import {
  fetchTomVendingRejectReport,
  finalizeStaleTomVendingSiteVisits,
  type TomVendingRejectReportRow,
} from '@/hooks/useTomVendingSiteVisitHistory';
import { useAuthData } from '@/hooks/getAuthData';
import { useNotification } from '@/contexts/NotificationContext';
import { useTomVendingLocationsList, tomVendingLocationIsWarehouse } from '@/hooks/useTomVendingLocation';
import { formatVisitDateLabel } from '../toms-vending/TomsVendingSiteVisitDetailContent';
import { downloadRejectsXlsx, TOMS_REPORT_EXPORT_MAX_ROWS } from '@/utils/tomsVendingReportExport';

const PAGE_SIZE = 50;

function defaultDateRange() {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  };
}

type LocRow = { id: number; name?: string; isWarehouse?: boolean };

function machineLabel(row: TomVendingRejectReportRow): string {
  if (row.source === 'warehouse') {
    return 'Warehouse';
  }
  const asset = row.machineAssetId?.trim();
  if (asset) {
    return asset;
  }
  if (row.vendingMachineId != null) {
    return `Machine ${row.vendingMachineId}`;
  }
  return '—';
}

function statusLabel(row: TomVendingRejectReportRow): string {
  if (row.source === 'warehouse') {
    return '—';
  }
  if (row.visitStatus === 'open') {
    return 'In progress';
  }
  if (row.visitStatus === 'finalized') {
    return 'Finalized';
  }
  return row.visitStatus ?? '—';
}

const TomsVendingRejectReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError, showSuccess } = useNotification();
  const { roles } = useAuthData();
  const hasAdminRole = roles.some((x) => x.name === 'Admin' || x.name === 'SystemAdmin');
  const { data: locations = [], isLoading: locLoading } = useTomVendingLocationsList();

  const init = useMemo(() => defaultDateRange(), []);
  const locationFromQuery = searchParams.get('locationId');
  const initialLocationId =
    locationFromQuery != null && !Number.isNaN(Number(locationFromQuery))
      ? Number(locationFromQuery)
      : '';

  const [dateFrom, setDateFrom] = useState(init.dateFrom);
  const [dateTo, setDateTo] = useState(init.dateTo);
  const [locationId, setLocationId] = useState<number | ''>(initialLocationId);
  const [rows, setRows] = useState<TomVendingRejectReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalRejectUnits, setTotalRejectUnits] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [finalizingStale, setFinalizingStale] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await fetchTomVendingRejectReport({
        dateFrom,
        dateTo,
        locationTomVendingId:
          locationId === '' || Number.isNaN(Number(locationId)) ? undefined : Number(locationId),
        skip: pageIndex * PAGE_SIZE,
        take: PAGE_SIZE,
      });
      setRows(r.rows);
      setTotal(r.total);
      setTotalRejectUnits(r.totalRejectUnits);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load reject report');
      setRows([]);
      setTotal(0);
      setTotalRejectUnits(0);
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo, locationId, pageIndex, showError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleGenerateReport = useCallback(async () => {
    setExporting(true);
    try {
      const locationTomVendingId =
        locationId === '' || Number.isNaN(Number(locationId)) ? undefined : Number(locationId);
      const chunks: TomVendingRejectReportRow[] = [];
      let skip = 0;
      let totalRows = 0;
      while (chunks.length < TOMS_REPORT_EXPORT_MAX_ROWS) {
        const r = await fetchTomVendingRejectReport({
          dateFrom,
          dateTo,
          locationTomVendingId,
          skip,
          take: TOMS_REPORT_EXPORT_MAX_ROWS,
        });
        totalRows = r.total;
        chunks.push(...r.rows);
        if (r.rows.length === 0 || chunks.length >= totalRows || skip + r.rows.length >= totalRows) {
          break;
        }
        skip += r.rows.length;
      }
      if (chunks.length === 0) {
        showError('Nothing to export for the selected date range.');
        return;
      }
      downloadRejectsXlsx(chunks, dateFrom, dateTo);
      if (totalRows > TOMS_REPORT_EXPORT_MAX_ROWS) {
        showSuccess(`Excel downloaded (${chunks.length.toLocaleString()} rows; capped at ${TOMS_REPORT_EXPORT_MAX_ROWS.toLocaleString()}).`);
      } else {
        showSuccess('Excel downloaded');
      }
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to generate report');
    } finally {
      setExporting(false);
    }
  }, [dateFrom, dateTo, locationId, showError, showSuccess]);

  const handleFinalizeStaleVisits = useCallback(async () => {
    setFinalizingStale(true);
    try {
      const r = await finalizeStaleTomVendingSiteVisits();
      if (r.errors.length > 0) {
        showError(`Finalized ${r.finalized} of ${r.attempted}; errors: ${r.errors.slice(0, 3).join('; ')}`);
      } else if (r.attempted === 0) {
        showSuccess('No stale visits to finalize.');
      } else {
        showSuccess(`Finalized ${r.finalized} of ${r.attempted} stale visit(s).`);
      }
      await fetchData();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to finalize stale visits');
    } finally {
      setFinalizingStale(false);
    }
  }, [fetchData, showError, showSuccess]);

  const locOpts = locations as LocRow[];
  const selectedLoc = locationId === '' ? null : locOpts.find((l) => l.id === locationId) ?? null;

  const openVisit = (row: TomVendingRejectReportRow) => {
    if (row.siteVisitId == null) {
      return;
    }
    navigate(
      `/app/toms-vending/site-visits/${row.siteVisitId}?locationId=${row.locationTomVendingId}`,
    );
  };

  const columns = useMemo<MRT_ColumnDef<TomVendingRejectReportRow>[]>(
    () => [
      {
        accessorKey: 'visitDate',
        header: 'Date',
        size: 120,
        Cell: ({ cell }) => formatVisitDateLabel(String(cell.getValue() ?? '')),
      },
      {
        accessorKey: 'locationName',
        header: 'Location',
        size: 180,
        Cell: ({ row }) => row.original.locationName?.trim() || `Location ${row.original.locationTomVendingId}`,
      },
      {
        accessorKey: 'source',
        header: 'Source',
        size: 120,
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.source === 'warehouse' ? 'Warehouse' : 'Site visit'}
            color={row.original.source === 'warehouse' ? 'secondary' : 'primary'}
            variant="outlined"
          />
        ),
      },
      {
        id: 'machine',
        header: 'Machine',
        size: 120,
        Cell: ({ row }) => machineLabel(row.original),
      },
      {
        accessorKey: 'itemName',
        header: 'Product',
        size: 200,
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      {
        accessorKey: 'upc',
        header: 'UPC',
        size: 120,
      },
      {
        accessorKey: 'rejectQty',
        header: 'Qty',
        size: 70,
      },
      {
        accessorKey: 'recordedByDisplayName',
        header: 'Recorded by',
        size: 140,
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      {
        id: 'visitStatus',
        header: 'Status',
        size: 110,
        Cell: ({ row }) => {
          if (row.original.source === 'warehouse') {
            return '—';
          }
          if (row.original.visitStatus === 'open') {
            return <Chip size="small" label="In progress" variant="outlined" />;
          }
          if (row.original.visitStatus === 'finalized') {
            return <Chip size="small" label="Finalized" variant="outlined" color="success" />;
          }
          return row.original.visitStatus ?? '—';
        },
      },
      {
        accessorKey: 'siteVisitId',
        header: 'Visit #',
        size: 90,
        Cell: ({ row }) => {
          const id = row.original.siteVisitId;
          if (id == null) {
            return '—';
          }
          return (
            <Link
              component={RouterLink}
              to={`/app/toms-vending/site-visits/${id}?locationId=${row.original.locationTomVendingId}`}
              onClick={(e) => e.stopPropagation()}
              sx={{ textDecoration: 'none' }}
            >
              {id}
            </Link>
          );
        },
      },
    ],
    [],
  );

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Reports', href: '/app/toms-vending/reports' },
    { label: 'Reject report' },
  ];

  return (
    <Box>
      <TitleBar breadcrumbs={breadcrumbs} title="Reject report" />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          Rejected units from site visits (after tallies are saved) and warehouse audits. Filter by
          date and location, then download Excel for the full result. Rejects appear as soon as
          they are saved. <strong>In progress</strong> means today&apos;s open service day; prior
          days finalize automatically after midnight.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPageIndex(0);
            }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPageIndex(0);
            }}
          />
          <Autocomplete<LocRow>
            size="small"
            sx={{ minWidth: 260 }}
            options={locOpts}
            loading={locLoading}
            value={selectedLoc}
            onChange={(_, v) => {
              setLocationId(v?.id ?? '');
              setPageIndex(0);
            }}
            getOptionLabel={(l) => l.name?.trim() || `Location ${l.id}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, l) => (
              <li {...props} key={l.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{l.name?.trim() || `Location ${l.id}`}</Typography>
                  {tomVendingLocationIsWarehouse(l) ? (
                    <Chip label="Warehouse" size="small" variant="outlined" color="secondary" />
                  ) : null}
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Location (optional)" />}
          />
          <Button variant="contained" size="small" onClick={() => void fetchData()} disabled={isLoading}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void handleGenerateReport()}
            disabled={isLoading || exporting}
          >
            {exporting ? 'Downloading…' : 'Download Excel'}
          </Button>
          {hasAdminRole ? (
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              disabled={isLoading || exporting || finalizingStale}
              onClick={() => void handleFinalizeStaleVisits()}
            >
              {finalizingStale ? 'Finalizing…' : 'Finalize stale visits'}
            </Button>
          ) : null}
          <Button
            component={RouterLink}
            to="/app/toms-vending/reports"
            variant="text"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            All reports
          </Button>
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>
          Total rejected units in range: <strong>{totalRejectUnits.toLocaleString()}</strong>
          {total > rows.length ? (
            <>
              {' '}
              · Showing {rows.length} of {total} lines
            </>
          ) : null}
        </Typography>

        <MaterialReactTable
          columns={columns}
          data={rows}
          manualPagination
          rowCount={total}
          enableGlobalFilter={false}
          onPaginationChange={(updater) => {
            setPageIndex(
              typeof updater === 'function'
                ? updater({ pageIndex, pageSize: PAGE_SIZE }).pageIndex
                : updater.pageIndex,
            );
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => openVisit(row.original),
            sx: { cursor: row.original.siteVisitId != null ? 'pointer' : 'default' },
          })}
          state={{
            isLoading,
            pagination: { pageIndex, pageSize: PAGE_SIZE },
          }}
          initialState={{ density: 'compact' }}
        />
      </Box>
    </Box>
  );
};

export default TomsVendingRejectReportPage;
